/**
 * Email Intake Queue API
 * All email intake queue-related API operations
 */

import { getStoredToken } from "../../../../services/api";
import {
  EmailIntakeQueueResponseDTO,
  UpdateStatusRequestDTO,
  UpdateStatusResponseDTO,
  EmailIntakeQueueDTO,
} from "../models/EmailIntakeQueueDTOs";
import { EmailStatus, EmailCategory } from "../../domain/entities/EmailIntakeQueueEntity";
import { API_BASE_URL } from "../../../../config/api";

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

// HTTP Status Constants
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

/**
 * Fetch emails with optional filters, pagination, and sorting
 */
export const fetchEmails = async (params?: {
  status?: EmailStatus;
  category?: EmailCategory;
  subdomain?: string;
  search?: string;
  reason?: string;
  limit?: number;
  offset?: number;
  sort_by?: 'created_at' | 'received_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
}): Promise<EmailIntakeQueueResponseDTO> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError('No authentication token found', HTTP_STATUS.UNAUTHORIZED);
  }

  // Build query parameters
  const queryParams = new URLSearchParams();
  
  if (params?.status) {
    queryParams.append('status', params.status);
  }
  
  if (params?.category) {
    queryParams.append('category', params.category);
  }
  
  if (params?.subdomain) {
    queryParams.append('subdomain', params.subdomain);
  }
  
  if (params?.search) {
    queryParams.append('search', params.search);
  }
  
  if (params?.reason) {
    queryParams.append('reason', params.reason);
  }
  
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }
  
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString());
  }
  
  if (params?.sort_by) {
    queryParams.append('sort_by', params.sort_by);
  }
  
  if (params?.sort_order) {
    queryParams.append('sort_order', params.sort_order);
  }

  const url = `${API_BASE_URL}/api/email-intake-queue${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || 'Failed to fetch emails',
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Update status for one or multiple emails
 */
export const updateEmailStatus = async (
  ids: number[],
  status: 'ignore' | 'pushed'
): Promise<UpdateStatusResponseDTO> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError('No authentication token found', HTTP_STATUS.UNAUTHORIZED);
  }

  const requestBody: UpdateStatusRequestDTO = {
    ids,
    status,
  };

  const response = await fetch(`${API_BASE_URL}/api/email-intake-queue/update-status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || 'Failed to update email status',
      response.status,
      errorData
    );
  }

  return response.json();
};

